/**
 * V2 Yoink page — one yoink, full size: the polaroid, the handwritten
 * title, the whole note, and where/who/what meta linking onward. This is
 * the unit people text each other, so it's a Share surface first.
 */
import { handleShareClick, canonicalUrl } from '../../ui/share.js';
import { buildShareCardSet, shareYoinkCard, JUST_CREATED_KEY } from '../../ui/share-card.js';
import { openPhotoViewer } from '../../ui/photo-viewer.js';
import { deleteYoink } from './manage.js';
import { fetchPersonalCategoriesByOwner } from '../journal/journal.js';
import { markComplete } from '../community/tutorial.js';

// Split 2026-07-25: yoink.js was 452 lines against the plan's ~400 budget.
// Reads moved to ./yoink-data.js and the render layer to ./yoink-view.js —
// straightforward here because renderYoink, like the place view, already
// took all of its inputs as parameters. socialLinkLabel is re-exported
// UNCHANGED; tests/v2-yoink and the panel suites import it from here.
import { fetchYoink, fetchCollectionContext, COLLECTION_CONTEXT_CAP } from './yoink-data.js';
import { slugify } from '../../../assets/js/shared/slugify.js';
import { setContext as setRibbonContext } from '../../ui/visitor-ribbon.js';
import {
  socialLinkLabel, imageUrls, createdBannerHtml, renderYoink,
} from './yoink-view.js';
import {
  fetchMine, fetchBalance, giveBravo, takeBackBravo, bravoFlashText,
} from '../bravo/bravo.js';

/** How long "2 left to give" stays up after a give or a take-back. */
const FLASH_MS = 4000;

export { socialLinkLabel };


/**
 * Mount the yoink view.
 * @param {HTMLElement} el
 * @param {object} ctx - { store, router, params: { id } }
 */
export function mount(el, ctx) {
  const { store, router, params } = ctx;
  let destroyed = false;
  let loaded = null;
  let confirming = false;
  let deleting = false;
  let photoIndex = 0;
  let lastSwipeAt = 0; // a swipe's trailing click must not open the viewer (V1 isSwiping)
  let touchX = null;
  let touchY = null;
  // Just landed here from "Save yoink"? Consume the handoff flag — the
  // moment happens once; a reload or a texted link never re-celebrates.
  let justCreated = false;
  let shareState = ''; // '' | 'building' | 'copied' | 'failed'
  let cardPromise = null;
  try {
    if (sessionStorage.getItem(JUST_CREATED_KEY) === params.id) {
      sessionStorage.removeItem(JUST_CREATED_KEY);
      justCreated = true;
    }
  } catch { /* private mode — no share moment, the nav Share still works */ }

  let collectionCtx = null; // { count, position, capped } — settles after first paint
  // { mine, balance, pending } — null until the reads land, and stays null
  // for good if the bravos migration hasn't been applied (bravo.js hides
  // every surface in that case, so nothing here needs to branch on it).
  let bravo = { mine: false, balance: null, pending: false, flash: '' };
  let bravoFlashTimer = null;

  function rerender() {
    renderYoink(el, loaded, store, confirming, photoIndex, deleting,
      justCreated ? createdBannerHtml(shareState, loaded?.title || 'A yoink', bravo.balance) : '',
      collectionCtx, bravo);
  }

  /**
   * Whether I've already given one, and how many I have left. Both are
   * cheap single-row reads and both fail silently — a bravo bar that
   * never appears is the designed outcome of an un-applied migration.
   */
  function loadBravoState(yoink) {
    const me = store.get().user;
    if (!me || me.id === (yoink.user_id || yoink.public_profiles?.id)) return;
    Promise.all([fetchMine(yoink.id, me.id), fetchBalance(me.id)]).then(([mine, balance]) => {
      if (destroyed) return;
      bravo = { mine: mine === true, balance, pending: false };
      rerender();
    }).catch(() => {});
  }

  /**
   * Give or take back. The count lives on the loaded row, so it moves
   * locally rather than costing a refetch; the balance comes back from
   * the server because the triggers own that arithmetic.
   */
  async function onBravo() {
    const me = store.get().user;
    if (!loaded || !me || bravo.pending) return;
    const giving = !bravo.mine;
    bravo = { ...bravo, pending: true };
    rerender();
    try {
      if (giving) await giveBravo(loaded.id, me.id);
      else await takeBackBravo(loaded.id, me.id);
      if (destroyed) return;
      loaded.bravo_count = Math.max(0, (loaded.bravo_count || 0) + (giving ? 1 : -1));
      const balance = await fetchBalance(me.id);
      if (destroyed) return;
      // Say what's left, once, then let it go — the answer to "how many do
      // I have?" belongs at the moment you just spent one, not on a
      // permanent counter.
      bravo = {
        mine: giving,
        balance,
        pending: false,
        flash: bravoFlashText(giving ? 'gave' : 'took-back', balance),
      };
      rerender();
      clearTimeout(bravoFlashTimer);
      bravoFlashTimer = setTimeout(() => {
        if (destroyed) return;
        bravo = { ...bravo, flash: '' };
        rerender();
      }, FLASH_MS);
    } catch (err) {
      if (destroyed) return;
      bravo = { ...bravo, pending: false, flash: '' };
      rerender();
      const errEl = el.querySelector('[data-bravo-error]');
      // The server's messages are already human ("No bravos left to give.")
      if (errEl) errEl.textContent = err?.message || 'That didn’t go through.';
    }
  }

  /**
   * "Stop #31 in Dru's Airports — see all 47": this yoink
   * placed in the owner's run of the category, linking to the filtered
   * journal (2026-07-26 landing-pages push step 3 — the strip that
   * upsells a one-photo share into the whole collection). A failed
   * fetch just leaves the strip off the page.
   */
  function loadCollectionContext(yoink) {
    const userId = yoink.user_id || yoink.public_profiles?.id;
    const categoryId = yoink.category_id || yoink.categories?.id;
    if (!userId || !categoryId) return;
    fetchCollectionContext(userId, categoryId).then((rows) => {
      if (destroyed || !rows.length) return;
      const index = rows.findIndex((r) => r.id === yoink.id);
      collectionCtx = {
        count: rows.length,
        capped: rows.length >= COLLECTION_CONTEXT_CAP,
        position: index >= 0 ? index + 1 : null,
      };
      rerender();
    }).catch(() => {});
  }

  el.innerHTML = '<div class="v2-loading">Unrolling the map…</div>';

  fetchYoink(params.id)
    .then((yoink) => {
      if (destroyed) return;
      if (!yoink) {
        el.innerHTML = '<div class="v2-empty">This yoink isn’t in the journal.</div>';
        return;
      }
      loaded = yoink;
      document.title = `${yoink.title || 'Yoink'} | Yoink Adventures v2`;
      const owner = yoink.public_profiles?.full_name || yoink.public_profiles?.username;
      if (owner) setRibbonContext(`${owner} collects the places they visit. Start your own journal.`);
      loadCollectionContext(yoink);
      loadBravoState(yoink);
      // A personal yoink names its shelf (§11): resolve the name from a
      // flat shelves read, never an embed — the chip just stays away if
      // the schema isn't there
      if (yoink.personal_category_id) {
        fetchPersonalCategoriesByOwner(yoink.user_id || yoink.public_profiles?.id).then((shelves) => {
          if (destroyed) return;
          const shelf = shelves.find((s) => s.id === yoink.personal_category_id);
          if (!shelf) return;
          loaded.personalShelf = shelf;
          rerender();
        });
      }
      // Start the cards while they admire the page: the photos are still
      // in the browser cache from the upload, so by tap time the files
      // are ready and share() stays inside Safari's activation window
      if (justCreated) cardPromise = buildShareCardSet(yoink);
      rerender();
    })
    .catch(() => {
      if (destroyed) return;
      el.innerHTML = '<div class="v2-error">Couldn’t reach the journal.</div>';
    });

  // The manage row depends on auth/profile, which can settle after render
  const unsubscribe = store.subscribe(() => {
    if (!destroyed && loaded) rerender();
  });

  /**
   * "Share photos" — the make-a-post intent, for EVERY visitor, not
   * just the creator, so any yoink can be shown off (or featured by
   * the Yoink account) as the full card set + clipboard caption. The
   * link-texting intent stays on the plain Share button next to it
   * (one payload per share — the sheet can't switch by target app).
   * The set is built lazily on first tap and memoized; the button
   * narrates while it renders.
   */
  let photosSharing = false;
  async function onSharePhotos(button) {
    if (!loaded || photosSharing) return;
    photosSharing = true;
    markComplete('share');
    const setLabel = (text) => {
      const label = el.querySelector('[data-share-photos-label]');
      if (label) label.textContent = text;
    };
    setLabel('Making your card…');
    if (!cardPromise) cardPromise = buildShareCardSet(loaded);
    const outcome = await shareYoinkCard(loaded, canonicalUrl(), cardPromise);
    photosSharing = false;
    if (destroyed) return;
    if (outcome === 'copied' || outcome === 'failed') {
      // 'failed' usually means the build outran the tap's activation
      // window — the cards are memoized now, so the next tap is instant
      setLabel(outcome === 'copied' ? 'Link copied' : 'Ready — tap again');
      setTimeout(() => {
        if (!destroyed && button.isConnected) setLabel('Share photos');
      }, 2500);
      return;
    }
    setLabel('Share photos');
  }

  async function onShareCard() {
    if (shareState === 'building') return;
    shareState = 'building';
    rerender();
    if (!cardPromise) cardPromise = buildShareCardSet(loaded);
    const outcome = await shareYoinkCard(loaded, canonicalUrl(), cardPromise);
    if (destroyed) return;
    if (outcome === 'shared') {
      markComplete('share');
      justCreated = false; // shown off — the moment is complete
    } else if (outcome === 'copied') {
      shareState = 'copied';
      rerender();
      setTimeout(() => {
        if (destroyed) return;
        justCreated = false;
        rerender();
      }, 2000);
      return;
    } else {
      // dismissed sheet or nothing available — keep the offer up
      shareState = outcome === 'failed' ? 'failed' : '';
    }
    rerender();
  }

  async function onDeleteConfirm() {
    if (deleting) return;
    deleting = true;
    rerender();
    try {
      await deleteYoink(loaded);
      if (!destroyed) router.navigate('discover');
    } catch (err) {
      deleting = false;
      if (destroyed) return;
      confirming = false;
      rerender();
      const errEl = el.querySelector('[data-manage-error]');
      if (errEl) errEl.textContent = err.message;
    }
  }

  function stepPhoto(delta) {
    if (!loaded) return;
    const urls = imageUrls(loaded);
    if (urls.length < 2) return;
    photoIndex = (photoIndex + delta + urls.length) % urls.length; // wrap like V1
    rerender();
  }

  function onClick(event) {
    // The plain Share = the universal link share (OG unfurl in texts)
    if (handleShareClick(event)) return;
    const photosBtn = event.target.closest('[data-share-photos]');
    if (photosBtn) {
      onSharePhotos(photosBtn);
      return;
    }
    if (event.target.closest('[data-share-card]')) {
      onShareCard();
      return;
    }
    if (event.target.closest('[data-share-card-skip]')) {
      justCreated = false;
      rerender();
      return;
    }
    const step = event.target.closest('[data-carousel-step]');
    if (step) {
      stepPhoto(Number(step.dataset.carouselStep));
      return;
    }
    const dot = event.target.closest('[data-carousel-dot]');
    if (dot) {
      photoIndex = Number(dot.dataset.carouselDot);
      rerender();
      return;
    }
    if (event.target.closest('[data-photo-open]')) {
      if (Date.now() - lastSwipeAt < 350) return; // it was a swipe, not a tap
      openPhotoViewer(imageUrls(loaded), photoIndex, { title: loaded?.title || '' });
      return;
    }
    if (event.target.closest('[data-bravo]')) {
      onBravo();
      return;
    }
    if (event.target.closest('[data-yoink-edit]')) {
      router.navigate('yoink-edit', { id: loaded.id });
      return;
    }
    if (event.target.closest('[data-yoink-delete-confirm]')) {
      onDeleteConfirm();
      return;
    }
    if (event.target.closest('[data-yoink-delete-cancel]')) {
      confirming = false;
      rerender();
      return;
    }
    if (event.target.closest('[data-yoink-delete]')) {
      confirming = true;
      rerender();
      return;
    }
    const userRow = event.target.closest('[data-username]');
    if (userRow) {
      router.navigate('user', { username: userRow.dataset.username });
      return;
    }
    const locationRow = event.target.closest('[data-location-id]');
    if (locationRow) {
      router.navigate('location', { id: locationRow.dataset.locationId });
      return;
    }
    const catRow = event.target.closest('[data-category-slug]');
    if (catRow) {
      router.navigate('category', { slug: catRow.dataset.categorySlug });
      return;
    }
    const personalRow = event.target.closest('[data-personal-slug]');
    if (personalRow) {
      router.navigate('personal-category', {
        username: personalRow.dataset.personalOwner, slug: personalRow.dataset.personalSlug,
      });
      return;
    }
    if (event.target.closest('[data-collection-journal]') && loaded) {
      // The owner's journal filtered to this category — the profile's
      // ?category= deep link, same as the showcase pages use
      const username = loaded.public_profiles?.username;
      const categoryName = loaded.categories?.name;
      if (username && categoryName) {
        router.navigate('user', { username }, { query: { category: slugify(categoryName) } });
      }
    }
  }

  function onKeydown(event) {
    // ←/→ page the carousel (V1 yoink-panel.js:561-571)
    if (event.key === 'ArrowLeft') return stepPhoto(-1);
    if (event.key === 'ArrowRight') return stepPhoto(1);
    if (event.key !== 'Enter' && event.key !== ' ') return;
    if (!event.target.closest('[data-username], [data-location-id], [data-category-slug]')) return;
    event.preventDefault();
    onClick(event);
  }

  // Carousel swipe (V1 yoink-panel.js:506-556): 50px horizontal, wraps
  function onTouchStart(event) {
    if (!event.target.closest('[data-photo-stage]')) return;
    touchX = event.touches[0]?.clientX ?? null;
    touchY = event.touches[0]?.clientY ?? null;
  }

  function onTouchEnd(event) {
    if (touchX == null) return;
    const dx = (event.changedTouches[0]?.clientX ?? touchX) - touchX;
    const dy = (event.changedTouches[0]?.clientY ?? touchY) - touchY;
    touchX = touchY = null;
    if (Math.abs(dx) < 50 || Math.abs(dx) < Math.abs(dy) + 10) return;
    lastSwipeAt = Date.now();
    stepPhoto(dx < 0 ? 1 : -1);
  }

  el.addEventListener('click', onClick);
  el.addEventListener('keydown', onKeydown);
  el.addEventListener('touchstart', onTouchStart, { passive: true });
  el.addEventListener('touchend', onTouchEnd, { passive: true });

  return () => {
    destroyed = true;
    clearTimeout(bravoFlashTimer);
    unsubscribe();
    el.removeEventListener('click', onClick);
    el.removeEventListener('keydown', onKeydown);
    el.removeEventListener('touchstart', onTouchStart);
    el.removeEventListener('touchend', onTouchEnd);
  };
}
